/**
 * Migration service for initializing gamification data for existing users
 */

import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './config';

// XP Constants
const XP_TARGET = 1000;

// Task definitions - must match DEFAULT_TASKS IDs
const TASKS = {
  register_account: { xp: 100, category: 'onboarding' },
  complete_profile: { xp: 30, category: 'onboarding' },
  take_skills_assessment: { xp: 100, category: 'foundation' },
  complete_first_free_course: { xp: 50, category: 'learning' },
  upgrade_account: { xp: 100, category: 'engagement' },
  complete_first_paid_course: { xp: 20, category: 'learning' },
  complete_5_paid_courses: { xp: 150, category: 'mastery' },
  login_3_days_streak: { xp: 30, category: 'habit' },
  login_5_days_streak: { xp: 50, category: 'habit' },
  finish_10_courses: { xp: 200, category: 'mastery' },
  invite_friend: { xp: 20, category: 'advocacy' },
  join_discord: { xp: 20, category: 'community' },
};

const ADDITIONAL_PROFILE_FIELDS = [
  'location',
  'jobTitle',
  'company',
  'website',
  'bio',
  'linkedIn',
  'linkedin',
  'twitter',
  'youtube'
] as const;

const countCompletedAdditionalProfileFields = (
  profile?: Record<string, unknown> | null
): number => {
  if (!profile) {
    return 0;
  }

  return ADDITIONAL_PROFILE_FIELDS.reduce((count, field) => {
    const value = profile[field];
    if (typeof value === 'string') {
      return value.trim() ? count + 1 : count;
    }
    return value ? count + 1 : count;
  }, 0);
};

export interface MigrationResult {
  success: boolean;
  message?: string;
  totalXP?: number;
  completedTasks?: number;
  readinessPercentage?: number;
  tasks?: string[];
}

/**
 * Initialize gamification data for an existing user
 * Checks for completed actions and awards appropriate XP
 */
export async function migrateUserGamification(userId: string): Promise<MigrationResult> {
  try {
    // eslint-disable-next-line no-console
    console.log('[Migration] Starting for user:', userId);
    
    // Check if user already has gamification data
    const gamificationRef = doc(db, 'userGamification', userId);
    const gamificationDoc = await getDoc(gamificationRef);
    
    if (gamificationDoc.exists()) {
      const existingData = gamificationDoc.data();
      // If user has 0 XP and no completed tasks, force re-migration
      // This handles cases where an empty document was created
      if (existingData.totalXP === 0 && (!existingData.completedTasks || existingData.completedTasks.length === 0)) {
        // eslint-disable-next-line no-console
        console.log('[Migration] User has empty gamification data, forcing re-migration');
        // Continue with migration below
      } else {
        // eslint-disable-next-line no-console
        console.log('[Migration] User already has gamification data with XP');
        return { 
          success: true, 
          message: 'Already initialized',
          totalXP: existingData.totalXP,
          completedTasks: existingData.completedTasks?.length || 0
        };
      }
    }
    
    // eslint-disable-next-line no-console
    console.log('[Migration] No existing data, starting migration');

    // Get user data to check completed actions
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // eslint-disable-next-line no-console
      console.log('[Migration] User document not found');
      return { success: false, message: 'User not found' };
    }

    const userData = userDoc.data();

    
    const completedTasks: string[] = [];
    let totalXP = 0;

    // Always award registration XP (user exists)
    completedTasks.push('register_account');
    totalXP += TASKS.register_account.xp;
    // eslint-disable-next-line no-console
    console.log('[Migration] Added register_account: +100 XP');


    // Check for profile completion - requires at least two additional fields
    const additionalFieldCount = countCompletedAdditionalProfileFields(userData);
    const hasProfile = additionalFieldCount >= 2;
    // eslint-disable-next-line no-console
    console.log('[Migration] Profile completion field count:', additionalFieldCount);

    if (hasProfile) {
      completedTasks.push('complete_profile');
      totalXP += TASKS.complete_profile.xp;
      // eslint-disable-next-line no-console
      console.log('[Migration] Added complete_profile: +30 XP, total:', totalXP);
    }

    // Check for skills assessment - query by userId field since doc ID is prefixed
    const assessmentQuery = query(
      collection(db, 'assessments'),
      where('userId', '==', userId),
      limit(1)
    );
    const assessmentSnapshot = await getDocs(assessmentQuery);
    
    // eslint-disable-next-line no-console
    console.log('[Migration] Assessment check:', { found: !assessmentSnapshot.empty });
    
    if (!assessmentSnapshot.empty) {
      completedTasks.push('take_skills_assessment');
      totalXP += TASKS.take_skills_assessment.xp;
      // eslint-disable-next-line no-console
      console.log('[Migration] Added take_skills_assessment: +100 XP, total:', totalXP);
    }

    // Check for course enrollments
    const progressQuery = query(
      collection(db, 'user_progress'),
      where('userId', '==', userId),
      limit(1)
    );
    const progressSnapshot = await getDocs(progressQuery);
    
    if (!progressSnapshot.empty) {
      // eslint-disable-next-line no-console
      console.log('[Migration] Found course progress');
      
      // Check for course completion
      const completedCourses = progressSnapshot.docs.filter(
        doc => doc.data().progress === 100
      );
      
      if (completedCourses.length > 0) {
        completedTasks.push('complete_first_free_course');
        totalXP += TASKS.complete_first_free_course.xp;
        // eslint-disable-next-line no-console
        console.log('[Migration] Added complete_first_free_course: +50 XP, total:', totalXP);
      }
    }

    // Check for subscription
    const subscriptionQuery = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId),
      where('status', 'in', ['active', 'trialing']),
      limit(1)
    );
    const subscriptionSnapshot = await getDocs(subscriptionQuery);
    
    // eslint-disable-next-line no-console
    console.log('[Migration] Subscription check:', { found: !subscriptionSnapshot.empty });
    
    if (!subscriptionSnapshot.empty) {
      completedTasks.push('upgrade_account');
      totalXP += TASKS.upgrade_account.xp;
      // eslint-disable-next-line no-console
      console.log('[Migration] Added upgrade_account: +100 XP, total:', totalXP);
    }

    // Calculate readiness percentage
    const readinessPercentage = Math.min((totalXP / XP_TARGET) * 100, 100);
    
    // eslint-disable-next-line no-console
    console.log('[Migration] Summary:', { 
      totalXP, 
      readinessPercentage: readinessPercentage.toFixed(1) + '%',
      completedTasks: completedTasks.length,
      tasks: completedTasks 
    });

    // Create or update gamification document
    await setDoc(gamificationRef, {
      userId,
      totalXP,
      readinessPercentage,
      completedTasks,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      migratedAt: serverTimestamp(),
    }, { merge: true });

    // eslint-disable-next-line no-console
    console.log('[Migration] ✅ Completed successfully');

    
    return {
      success: true,
      totalXP,
      completedTasks: completedTasks.length,
      readinessPercentage,
      tasks: completedTasks,
    };
  } catch (error) {
    console.error(`[Migration] ❌ Error for user ${userId}:`, error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
